// ── industryBenchmark.js — 외식업 평균 벤치마크 데이터 ───────────
// 한국외식업중앙회 · 통계청 · 한국푸드테크협회 등 공개 통계 종합 (2024~2025)
// 정확한 수치는 업종/규모/지역 따라 편차 큼. 참고 용도.
//
// direction:
//   higher = 높을수록 좋음 (매출, 마진율 등)
//   lower  = 낮을수록 좋음 (원가율, 인건비율 등)

export const INDUSTRY_BENCHMARKS = {
  food_service: {
    label: '외식업 (음식점)',
    indicators: {
      netMargin: {
        label: '순이익률',
        avg: 12,           // %  — 외식업 평균 순이익률 (영업이익 기준 더 낮음)
        good: 20,          // % 이상이면 우수
        bad: 5,            // % 미만이면 위험
        direction: 'higher',
        unit: '%',
        source: '한국외식산업연구원 2024',
      },
      foodCost: {
        label: '식재료 원가율',
        avg: 35,           // %  — 한식 38, 양식 30, 카페 25 정도
        good: 30,          // % 이하 우수
        bad: 42,           // % 이상 위험
        direction: 'lower',
        unit: '%',
        source: '외식업 표준 원가구조 (2024)',
      },
      laborCost: {
        label: '인건비 비율',
        avg: 28,           // %
        good: 22,          // % 이하 우수
        bad: 35,           // % 이상 위험
        direction: 'lower',
        unit: '%',
        source: '소상공인시장진흥공단 2024',
      },
      revenueGrowth: {
        label: '월 매출 성장률',
        avg: 3,            // %  (전년 동월 대비, 경기 영향)
        good: 8,           // %
        bad: -5,           // %
        direction: 'higher',
        unit: '%',
        source: '통계청 서비스업동향조사 2025',
      },
    },
  },
};

// ── 본인 vs 평균 비교 결과 계산 ──
// actual: 사용자 실측치
// returns: { delta, pctDiff, status: 'good'|'avg'|'warn'|'bad', label }
export function compareToBenchmark(indicatorKey, actual, industryKey = 'food_service') {
  const ind = INDUSTRY_BENCHMARKS[industryKey]?.indicators?.[indicatorKey];
  if (!ind || actual === null || actual === undefined) {
    return { delta: null, pctDiff: null, status: 'unknown', label: '—' };
  }
  const delta = actual - ind.avg;
  const pctDiff = ind.avg !== 0 ? (delta / ind.avg) * 100 : null;

  let status, label;
  if (ind.direction === 'higher') {
    if (actual >= ind.good)      { status = 'good'; label = '우수'; }
    else if (actual >= ind.avg)  { status = 'avg';  label = '평균 이상'; }
    else if (actual >= ind.bad)  { status = 'warn'; label = '평균 미만'; }
    else                          { status = 'bad';  label = '위험'; }
  } else {
    // lower = good
    if (actual <= ind.good)      { status = 'good'; label = '우수'; }
    else if (actual <= ind.avg)  { status = 'avg';  label = '평균 이하'; }
    else if (actual <= ind.bad)  { status = 'warn'; label = '평균 초과'; }
    else                          { status = 'bad';  label = '위험'; }
  }
  return { delta, pctDiff, status, label, avg: ind.avg, direction: ind.direction };
}

// ── 현재 월 매출 run-rate 외삽 (월 단위 비용과 같은 기간 비교용) ──
function applyRunRate(actualRevenue, monthKey) {
  if (!monthKey || actualRevenue <= 0) return { revenue: actualRevenue, projected: false };
  const now = new Date();
  const todayMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (monthKey !== todayMonthKey) return { revenue: actualRevenue, projected: false };
  const todayDay = now.getDate();
  if (todayDay < 7) return { revenue: actualRevenue, projected: false };
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return { revenue: Math.round(actualRevenue * (daysInMonth / todayDay)), projected: true, todayDay, daysInMonth };
}

// ── 매장 데이터 → 4가지 지표 추출 ──
export function calcUserMetrics({ profitLoss, monthKey, prevMonthKey }) {
  const cur = profitLoss?.[monthKey] || {};
  const prev = profitLoss?.[prevMonthKey] || {};
  const r = cur?.revenue || {};
  const c = cur?.costs || {};
  const actualRevenue = (Number(r.card) || 0) + (Number(r.cash) || 0) + (Number(r.delivery) || 0);
  const rr = applyRunRate(actualRevenue, monthKey);
  const totalRevenue = rr.revenue;
  const ingredients = Number(c.ingredients) || 0;
  const labor = Number(c.labor) || 0;
  const totalCosts = ingredients + labor + (Number(c.rent) || 0) + (Number(c.utilities) || 0) + (Number(c.other) || 0);
  const profit = totalRevenue - totalCosts;
  const netMargin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : null;
  const foodCost = totalRevenue > 0 ? (ingredients / totalRevenue) * 100 : null;
  const laborCost = totalRevenue > 0 ? (labor / totalRevenue) * 100 : null;

  const pr = prev?.revenue || {};
  const prevRevenue = (Number(pr.card) || 0) + (Number(pr.cash) || 0) + (Number(pr.delivery) || 0);
  const revenueGrowth = prevRevenue > 0
    ? ((totalRevenue - prevRevenue) / prevRevenue) * 100
    : null;

  return { netMargin, foodCost, laborCost, revenueGrowth, _projected: rr.projected, _todayDay: rr.todayDay, _daysInMonth: rr.daysInMonth };
}

export const STATUS_COLORS = {
  good:    { bg: '#FAFAFA', border: '#FED7AA', text: '#FF6F0F' },
  avg:     { bg: '#f0f9ff', border: '#7dd3fc', text: '#0369a1' },
  warn:    { bg: '#FAFAFA', border: '#D4D4D8', text: '#EA580C' },
  bad:     { bg: '#fef2f2', border: '#fca5a5', text: '#52525B' },
  unknown: { bg: '#f3f4f6', border: '#e5e7eb', text: '#9ca3af' },
};
